/**
 * ONE SEPARATOR RULE FOR THE STORYTELLER'S STATUS STRING.
 *
 * A summoner's or card's status arrives as one free-form string authored by the
 * storyteller, and THREE surfaces cut it into effects: the summoner rail
 * (indicators.js), the card plate (cards.js) and the status-change reaction
 * (fx.js). Each owned its own `split()`, and they had drifted: a 2026-08-08 fix
 * taught the summoner rail that a PIPE separates effects — because the model
 * writes one — and left the other two splitting on `;` alone. A pipe therefore
 * still printed as the first glyph of a card's status chip.
 *
 * This module is the single rule. It segments only; each surface keeps its own
 * cleaning (the card plate strips a trailing period, the summoner rail strips
 * `**` anywhere) because those differ deliberately.
 */

/* VERTICAL-BAR PUNCTUATION ONLY — never a letter, and that exclusion is the
   whole care in this list. `丨` (U+4E28) is a CJK ideograph and `ǀ` (U+01C0) is
   a Latin letter used in several African orthographies; both LOOK like a bar
   and cutting a status on either would silently mangle real wording, which is
   the one thing this file must not do. `∣` (U+2223) is a maths operator and is
   left out for the same reason. What remains cannot occur inside a word:

     |  U+007C  vertical line          ｜ U+FF5C  fullwidth vertical line
     ¦  U+00A6  broken bar             ‖  U+2016  double vertical line
     │  U+2502  box drawings light vertical
     ┃  U+2503  box drawings heavy vertical
     ║  U+2551  box drawings double vertical

   U+FF5C is the one that matters in practice: it is the SAME character in its
   fullwidth form, which is what a CJK keyboard produces, and Moonfall ships ja
   and zh-CN. */
const STATUS_SEPARATORS = /[;|｜¦‖│┃║]/;

/* A SEGMENT WITH NO VISIBLE GLYPH IS NOT AN EFFECT.
   `.trim()` removes whitespace; it does not remove zero-width or format
   characters, and every one of them is truthy — so a segment made only of those
   survived a `filter(Boolean)` and became a chip that rendered no text. On a
   landscape phone a chip has no pill to draw, so its only ink was its own accent
   edge: a bare vertical hairline in front of the status. Dropping the segment
   removes nothing a player could read. Characters INSIDE a real effect are
   never touched — this matches the whole segment or nothing. */
const RENDERS_NOTHING = /^[\s\p{Cf}\u034F]*$/u;

/**
 * Cut a status string into raw, trimmed segments, dropping any that would
 * render no glyph. Wording is passed through untouched; only where the string
 * is cut is decided here.
 */
function splitStatusSegments(status) {
  return String(status == null ? '' : status)
    .split(STATUS_SEPARATORS)
    .map((raw) => raw.trim())
    .filter((segment) => !RENDERS_NOTHING.test(segment));
}

/**
 * The card plate's and the reaction layer's shared cleaning: drop the
 * storyteller's bold markers when they WRAP the whole token, and drop one
 * sentence-ending period. (The summoner rail deliberately differs — it strips
 * `**` anywhere in an effect — so it does not use this.)
 *
 * ORDER USED TO DECIDE THE OUTCOME, AND IT LOST EITHER WAY. Both surfaces ran
 * the marker test before the period test, so `**Shielded**.` failed
 * `endsWith('**')`, kept its markers, and rendered the literal asterisks to the
 * player. Swapping the two only moves the failure to `**Shielded.**`, where the
 * period is inside the markers. Peeling until nothing more comes off handles
 * both spellings and cannot loop: every pass that changes anything removes at
 * least one character.
 */
function cleanStatusToken(raw) {
  let token = String(raw == null ? '' : raw).trim();
  for (let pass = 0; pass < 4; pass += 1) {
    const before = token;
    if (token.endsWith('.')) token = token.slice(0, -1).trim();
    /* `>= 4` so `****` — markers with nothing between them — peels to '' and
       is dropped by the caller rather than rendering an empty chip. */
    if (token.length >= 4 && token.startsWith('**') && token.endsWith('**')) {
      token = token.slice(2, -2).trim();
    }
    if (token === before) break;
  }
  return token;
}

export { STATUS_SEPARATORS, RENDERS_NOTHING, splitStatusSegments, cleanStatusToken };
